// Transitional: the deploy-time `runtime-config.js` shape. Read only as a
// fallback while the build is migrated to bake the values into Vite `.env`
// (#1510); removed once the build sets every VITE_* var.
interface RuntimeConfig {
  cognitoPool: string;
  cognitoClient: string;
  region: string;
  environment: string;
  release: string;
  sentry?: { dsn: string };
  hes: { cme: string; cme2100: string };
}

declare global {
  interface Window {
    runConfig?: RuntimeConfig;
  }
}

export interface AppConfig {
  cognitoPool: string;
  cognitoClient: string;
  region: string;
  environment: string;
  release: string;
  sentryDsn?: string;
  apiUrl: string;
  webSocketUrl: string;
  publicAppUrl?: string;
  hes: { cme: string; cme2100: string };
}

let cached: AppConfig | undefined;

// Build the config from Vite `.env` (build-time) first, falling back to the
// injected `window.runConfig` during the migration to env-only config (#1510).
function build(): AppConfig {
  const env = import.meta.env;
  const rc = window.runConfig;
  const cfg: AppConfig = {
    cognitoPool: env?.VITE_COGNITO_POOL_ID || rc?.cognitoPool || "",
    cognitoClient: env?.VITE_COGNITO_CLIENT_ID || rc?.cognitoClient || "",
    region: env?.VITE_REGION || rc?.region || "",
    environment: env?.VITE_ENVIRONMENT || rc?.environment || "",
    release: env?.VITE_RELEASE || rc?.release || "dev",
    sentryDsn: env?.VITE_SENTRY_DSN || rc?.sentry?.dsn,
    apiUrl: env?.VITE_API_URL || window.localStorage.getItem("URL") || "",
    webSocketUrl: env?.VITE_WEB_SOCKET_URL || "",
    publicAppUrl: env?.VITE_PUBLIC_APP_URL,
    hes: {
      cme: env?.VITE_HES_CME || rc?.hes?.cme || "",
      cme2100: env?.VITE_HES_CME2100 || rc?.hes?.cme2100 || "",
    },
  };
  const missing = (["cognitoPool", "cognitoClient", "apiUrl"] as const).filter(
    (k) => !cfg[k],
  );
  if (missing.length > 0 && env?.MODE !== "test") {
    console.error(
      `[config] missing required values: ${missing.join(", ")} — check the ` +
        "VITE_* build env (or runtime-config during the migration).",
    );
  }
  return cfg;
}

/** The validated app config, sourced from Vite `.env` (build-time). Cached. */
export function appConfig(): AppConfig {
  if (!cached) {
    cached = build();
  }
  return cached;
}

export const getApiUrl = (): string => appConfig().apiUrl;

export const appUrl = (): string => appConfig().publicAppUrl ?? "";

export const getWebsocketBaseUrl = (): string => appConfig().webSocketUrl;

export const getCognitoConfig = () => {
  const cfg = appConfig();
  return {
    REGION: cfg.region,
    USER_POOL_ID: cfg.cognitoPool,
    APP_CLIENT_ID: cfg.cognitoClient,
  };
};

const config = {
  apiGateway: {
    REGION: appConfig().region,
    URL: getApiUrl(),
  },
  cognito: getCognitoConfig(),
};

export default config;
